import { create } from 'zustand';

import {
  ApiTaskDocumentResponseV2,
  ApiTaskResponseV2,
} from '@/shared/api/generated/data-contracts';

export type TasksState = {
  tasks: ApiTaskResponseV2[];
  tasksCreatedByMe: ApiTaskResponseV2[];
  isLoading: boolean;
  isInitialized: boolean;
  isMyCreatedTasksInitialized: boolean;
};

export type TasksStore = TasksState & {
  setTasks: (tasks: ApiTaskResponseV2[]) => void;
  setMyCreatedTasks: (tasks: ApiTaskResponseV2[]) => void;
  addTask: (newTask: ApiTaskResponseV2, profileId: number) => void;
  updateTask: (taskId: string, updates: Omit<Partial<ApiTaskResponseV2>, 'id'>) => void;
  removeTask: (taskId: string) => void;
  updateTaskDocuments: (taskId: string, documents: ApiTaskDocumentResponseV2[]) => void;
  removeTaskDocument: (taskId: string, documentId: string) => void;
  setLoading: (isLoading: boolean) => void;
  setInitialized: (isInitialized: boolean) => void;
};

export const useTasksStore = create<TasksStore>((set, get) => ({
  tasks: [],
  tasksCreatedByMe: [],
  isLoading: false,
  isInitialized: false,
  isMyCreatedTasksInitialized: false,

  setTasks: (tasks) => set({ tasks }),
  setMyCreatedTasks: (myCreatedTasks) =>
    set({ tasksCreatedByMe: myCreatedTasks, isMyCreatedTasksInitialized: true }),
  addTask: (newTask, profileId) =>
    set((state) => {
      const updateList = (list: ApiTaskResponseV2[]) => [newTask, ...list];

      return {
        tasks: updateList(state.tasks),
        tasksCreatedByMe:
          state.isMyCreatedTasksInitialized && newTask.creator?.profileId === profileId
            ? updateList(state.tasksCreatedByMe)
            : state.tasksCreatedByMe,
      };
    }),
  updateTask: (taskId, data) =>
    set(() => {
      const state = get();
      const updateList = (list: ApiTaskResponseV2[]) =>
        list.map((t) => (t.id === taskId ? { ...t, ...data } : t));

      return {
        tasks: updateList(state.tasks),
        tasksCreatedByMe: updateList(state.tasksCreatedByMe),
      };
    }),
  removeTask: (taskId) =>
    set(() => {
      const state = get();
      const filterList = (list: ApiTaskResponseV2[]) => list.filter((t) => t.id !== taskId);

      return {
        tasks: filterList(state.tasks),
        tasksCreatedByMe: filterList(state.tasksCreatedByMe),
      };
    }),
  updateTaskDocuments: (taskId, documentIds) =>
    set(() => {
      const state = get();
      const updateList = (list: ApiTaskResponseV2[]) =>
        list.map((item) => (item.id === taskId ? { ...item, documents: documentIds } : item));

      return {
        tasks: updateList(state.tasks),
        tasksCreatedByMe: updateList(state.tasksCreatedByMe),
      };
    }),
  removeTaskDocument: (taskId, documentId) =>
    set(() => {
      const state = get();
      const updateList = (list: ApiTaskResponseV2[]) =>
        list.map((item) =>
          item.id === taskId
            ? { ...item, documents: item.documents.filter((doc) => doc.id !== documentId) }
            : item,
        );

      return {
        tasks: updateList(state.tasks),
        tasksCreatedByMe: updateList(state.tasksCreatedByMe),
      };
    }),
  setLoading: (isLoading) => set({ isLoading }),
  setInitialized: (isInitialized) => set({ isInitialized }),
}));
